import { z } from "zod";

export const choreSchema = z.object({
  id: z.string(),
  choreScheduleId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  expectedDurationMins: z.number().nullable(),
  googleCalendarEventId: z.string().nullable(),
  googleCalendarHtmlLink: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type Chore = z.infer<typeof choreSchema>;

const dayOfWeekSchema = z.enum([
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
]);

const intervalSchema = z.number().int().min(1).optional();

export const scheduleSchema = z.discriminatedUnion("frequency", [
  z.object({ frequency: z.literal("daily"), interval: intervalSchema }),
  z.object({
    frequency: z.literal("weekly"),
    interval: intervalSchema,
    onDays: z.array(dayOfWeekSchema).min(1).optional(),
  }),
  z.object({ frequency: z.literal("monthly"), interval: intervalSchema }),
]);

export type Schedule = z.infer<typeof scheduleSchema>;

export const createChoreInputSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  expectedDurationMins: z.number().int().positive(),
  startDateTime: z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/),
  timezone: z.string().min(1),
  schedule: scheduleSchema,
});

export type CreateChoreInput = z.infer<typeof createChoreInputSchema>;

export async function listChores(choreScheduleId: string): Promise<Chore[]> {
  const response = await fetch(`/api/chore-schedules/${choreScheduleId}/chores`, {
    credentials: "include",
  });
  if (!response.ok) throw new Error("Failed to fetch chores");
  return choreSchema.array().parse(await response.json());
}

export async function getChore(choreScheduleId: string, choreId: string): Promise<Chore> {
  const response = await fetch(`/api/chore-schedules/${choreScheduleId}/chores/${choreId}`, {
    method: "GET",
    credentials: "include",
  });
  if (!response.ok) throw new Error("Failed to get chore");
  return choreSchema.parse(await response.json());
}

export async function deleteChore(choreScheduleId: string, choreId: string): Promise<void> {
  const response = await fetch(`/api/chore-schedules/${choreScheduleId}/chores/${choreId}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!response.ok) throw new Error("Failed to delete chore");
}

export async function createChore(choreScheduleId: string, data: CreateChoreInput): Promise<Chore> {
  const response = await fetch(`/api/chore-schedules/${choreScheduleId}/chores`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error("Failed to create chore");
  return choreSchema.parse(await response.json());
}

export const choreCompletionSchema = z.object({
  id: z.string(),
  choreId: z.string(),
  actualDurationMins: z.number().nullable(),
  googleCalendarEventId: z.string().nullable(),
  googleCalendarHtmlLink: z.string().nullable(),
  completedAt: z.coerce.date(),
  createdAt: z.coerce.date(),
});

export type ChoreCompletion = z.infer<typeof choreCompletionSchema>;

export const createChoreCompletionInputSchema = z.object({
  actualDurationMins: z.number().int().positive(),
  completedAt: z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/),
  timezone: z.string().min(1),
});

export type CreateChoreCompletionInput = z.infer<typeof createChoreCompletionInputSchema>;

export async function listChoreCompletions(
  choreScheduleId: string,
  choreId: string,
  params?: { limit?: number; offset?: number },
): Promise<ChoreCompletion[]> {
  const url = new URL(
    `/api/chore-schedules/${choreScheduleId}/chores/${choreId}/completions`,
    window.location.origin,
  );
  if (params?.limit !== undefined) url.searchParams.set("limit", String(params.limit));
  if (params?.offset !== undefined) url.searchParams.set("offset", String(params.offset));
  const response = await fetch(url.toString(), { credentials: "include" });
  if (!response.ok) throw new Error("Failed to fetch chore completions");
  return choreCompletionSchema.array().parse(await response.json());
}

export async function createChoreCompletion(
  choreScheduleId: string,
  choreId: string,
  data: CreateChoreCompletionInput,
): Promise<ChoreCompletion> {
  const response = await fetch(
    `/api/chore-schedules/${choreScheduleId}/chores/${choreId}/completions`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    },
  );
  if (!response.ok) throw new Error("Failed to create chore completion");
  return choreCompletionSchema.parse(await response.json());
}

export const choreHistoryItemSchema = z.object({
  completionId: z.string(),
  choreId: z.string(),
  choreScheduleId: z.string(),
  choreName: z.string(),
  expectedDurationMins: z.number().nullable(),
  actualDurationMins: z.number().nullable(),
  completionGoogleCalendarHtmlLink: z.string().nullable(),
  completedAt: z.coerce.date(),
});

export type ChoreHistoryItem = z.infer<typeof choreHistoryItemSchema>;

export async function listChoreHistory(params?: {
  userId?: string | undefined;
  limit?: number;
  offset?: number;
}): Promise<ChoreHistoryItem[]> {
  const url = new URL("/api/chore-history", window.location.origin);
  if (params?.userId !== undefined) url.searchParams.set("userId", params.userId);
  if (params?.limit !== undefined) url.searchParams.set("limit", String(params.limit));
  if (params?.offset !== undefined) url.searchParams.set("offset", String(params.offset));
  const response = await fetch(url.toString(), { credentials: "include" });
  if (!response.ok) throw new Error("Failed to fetch chore history");
  return choreHistoryItemSchema.array().parse(await response.json());
}
